import React, { useEffect, useState } from "react";
import { Button, Modal, Form, Spinner } from "react-bootstrap";
import { useFormik } from "formik";
import { tutorialSchema } from "../../../../../helpers/yup_validator";
import red_cross from "../../../../../Assets/svg/red-cross.svg";
import { post, put } from "../../../../../helpers/api_helper";
import toast from "react-hot-toast";

const TutorialPop = ({
  showModal,
  setShowModal,
  setRefresh,
  mode,
  tutorialData,
  resetFormOnClose,
}) => {
  const [isUploading, setIsUploading] = useState(false);

  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      media_type: "video",
      media: "",
      paid: false,
      status: "active",
    },
    validationSchema: tutorialSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      try {
        if (mode === "create") {
          await post("/tutorial", values);
          formik.resetForm();
          toast.success("Tutorial created successfully");
        } else if (mode === "edit") {
          await put(`/tutorial/${tutorialData._id}`, values);
          toast.success("Tutorial updated successfully");
        }
        setShowModal(false);
        setRefresh();
      } catch (error) {
        console.error("Error submitting tutorial data:", error);
        setErrors({
          submissionError: error.response?.data?.message
            ? error.response?.data?.message
            : error.response?.data?.error || "An error occurred.",
        });
        toast.error(
          error.response?.data?.message
            ? error.response?.data?.message
            : error.response?.data?.error || "An error occurred."
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (mode === "edit" && tutorialData) {
      formik.setValues({
        title: tutorialData.title || "",
        description: tutorialData.description || "",
        media_type: tutorialData.media_type || "video",
        media: tutorialData.media || "",
        paid: tutorialData.paid || false,
        status: tutorialData.status || "active",
      });
    } else if (resetFormOnClose) {
      formik.resetForm();
    }
  }, [mode, tutorialData, resetFormOnClose]);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setIsUploading(true);
      try {
        const formData = new FormData();
        formData.append("file", file);

        const response = await post("/tutorial/upload", formData);
        formik.setFieldValue("media", response.data.fileUrl);
      } catch (error) {
        console.error("Error uploading file:", error);
      } finally {
        setIsUploading(false);
      }
    }
  };

  return (
    <Modal
      show={showModal}
      onHide={() => setShowModal(false)}
      backdrop="static"
      keyboard={false}
      centered
      scrollable
    >
      <Modal.Body>
        <div className="topHeader d-flex align-items-start justify-content-between gap-10 pb-2">
          <div className="left">
            <h5 className="m-0 fw-sbold">
              {mode === "edit" ? "Edit Tutorial" : "Add New Tutorial"}
            </h5>
            <p className="m-0 text-muted">
              {mode === "edit"
                ? "Update tutorial details below."
                : "Fill in the details to create a new tutorial."}
            </p>
          </div>
          <Button
            className="p-0 border-0"
            variant="transparent"
            onClick={() => setShowModal(false)}
          >
            <img src={red_cross} alt="Close" />
          </Button>
        </div>
        <div className="FormWrapper">
          {formik.errors.submissionError && (
            <div className="alert alert-danger">
              {formik.errors.submissionError}
            </div>
          )}
          <Form onSubmit={formik.handleSubmit}>
            <Form.Group controlId="title" className="my-2">
              <Form.Label className="text-dark m-0 pb-1">Title</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={formik.values.title}
                placeholder="Tutorial Title"
                isInvalid={formik.touched.title && formik.errors.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.title}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group controlId="description" className="my-2">
              <Form.Label className="text-dark m-0 pb-1">
                Description
              </Form.Label>
              <Form.Control
                as="textarea"
                name="description"
                value={formik.values.description}
                placeholder="Tutorial Description"
                isInvalid={
                  formik.touched.description && formik.errors.description
                }
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.description}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group controlId="media_type" className="my-2">
              <Form.Label className="text-dark m-0 pb-1">Media Type</Form.Label>
              <Form.Control
                as="select"
                name="media_type"
                value={formik.values.media_type}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <option value="video">Video</option>
                <option value="pdf">PDF</option>
              </Form.Control>
            </Form.Group>
            <Form.Group controlId="media" className="my-2">
              <Form.Label className="text-dark m-0 pb-1">Media File</Form.Label>
              <Form.Control
                type="file"
                name="media"
                isInvalid={formik.touched.media && formik.errors.media}
                onChange={handleFileChange}
                onBlur={formik.handleBlur}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.media}
              </Form.Control.Feedback>
              {isUploading && (
                <div className="d-flex justify-content-center my-2">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Uploading...</span>
                </div>
              )}
            </Form.Group>
            <Form.Group controlId="paid" className="my-2">
              <Form.Check
                type="checkbox"
                name="paid"
                label="Paid Tutorial"
                checked={formik.values.paid}
                onChange={formik.handleChange}
              />
            </Form.Group>
            <Form.Group controlId="status" className="my-2">
              <Form.Label className="text-dark m-0 pb-1">Status</Form.Label>
              <Form.Check
                type="switch"
                id="status"
                name="status"
                label={
                  formik.values.status === "active" ? "Active" : "Inactive"
                }
                checked={formik.values.status === "active"}
                onChange={(e) =>
                  formik.setFieldValue(
                    "status",
                    e.target.checked ? "active" : "inactive"
                  )
                }
              />
            </Form.Group>
            <Button
              className="d-flex align-items-center w-100 justify-content-center commonBtn"
              type="submit"
              disabled={formik.isSubmitting}
            >
              {mode === "create" ? "Add Tutorial" : "Update Tutorial"}
            </Button>
          </Form>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default TutorialPop;
